<template>
  <div v-if="isShowLocaleButton" class="relative">
    <button
      ref="buttonRef"
      class="flex w-full items-center gap-3 rounded-xl border-2 border-solid border-white bg-gray-600_a15 px-3 py-2 text-white hover:bg-gray-600_a25 active:bg-gray-600_a35 tablet:px-5 tablet:py-3"
      :title="$t('region.change-region')"
      @click="isShowMenu = !isShowMenu"
    >
      <IconLang class="h-6 w-6 shrink-0 tablet:h-7 tablet:w-7" />
      <span
        class="flex-1 truncate text-left text-sm leading-7 tablet:text-base"
      >
        {{ currentLabel }}
      </span>
      <IconCaret
        :direction="isShowMenu ? 'up' : 'down'"
        class="h-6 w-6 shrink-0 tablet:h-7 tablet:w-7"
      />
    </button>

    <div
      v-show="isShowMenu"
      ref="menuRef"
      class="container-scrollbar absolute bottom-full left-0 right-0 z-10 mb-2 max-h-96 overflow-y-auto rounded-lg bg-white py-6 shadow-button shadow-black-000_a5 tablet:rounded-[10px] desktop:rounded-xl desktop:p-3 desktop:py-3"
    >
      <ul>
        <li
          v-for="region in regionList"
          :key="region.regionCode"
          class="w-full"
        >
          <div
            v-if="isDesktop"
            class="rounded-xl px-5 pb-4 pt-5"
            :class="
              regionList.length > 1 &&
              'hover:bg-gray-600_a25 active:bg-gray-600_a35'
            "
          >
            <p class="mb-3 text-base font-bold text-black">
              {{ provideRegionName(region.regionCode) }}
            </p>
            <div class="flex flex-wrap items-center gap-3">
              <template
                v-for="(site, index) in region.websites"
                :key="site.code"
              >
                <span v-if="index > 0" class="text-sm text-gray-700">｜</span>
                <a
                  class="text-base font-bold text-blue-400 underline hover:text-blue-500"
                  :href="provideLocalePath(site.code)"
                  :title="provideLocaleName(site.code)"
                >
                  {{ provideLocaleName(site.code) }}
                </a>
              </template>
            </div>
          </div>

          <template v-else>
            <div
              class="flex items-center gap-3 px-3 py-3 tablet:px-4 tablet:py-4"
            >
              <span
                class="whitespace-nowrap text-sm text-gray-600 tablet:text-base"
              >
                {{ provideRegionName(region.regionCode) }}
              </span>
              <span class="h-px flex-1 bg-gray-400" />
            </div>
            <a
              v-for="site in region.websites"
              :key="site.code"
              class="block w-full rounded-lg px-3 py-3 text-sm font-bold text-black hover:bg-gray-600_a35 tablet:rounded-[10px] tablet:px-4 tablet:py-4 tablet:text-base"
              :href="provideLocalePath(site.code)"
              :title="provideLocaleName(site.code)"
            >
              {{ provideLocaleName(site.code) }}
            </a>
          </template>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onClickOutside } from '@vueuse/core';

  import type { IRegion } from '@/assets/types/api/Common';

  const { locale } = useI18n();
  const {
    isShowLocaleButton,
    contextRegions,
    currentRegion,
    provideLocalePath,
    provideLocaleName,
    provideRegionName,
  } = useLanguage();

  const { isDesktop } = useScreen();

  const buttonRef = ref<HTMLElement | null>(null);
  const menuRef = ref<HTMLElement | null>(null);
  const isShowMenu = ref<boolean>(false);

  // 登入後只能切換目前站點所屬區域的語系，未登入則可切換所有區域
  const regionList = computed((): IRegion[] => contextRegions.value);

  const currentLabel = computed((): string => {
    const regionName = currentRegion.value
      ? provideRegionName(currentRegion.value)
      : '';
    const localeName = provideLocaleName(locale.value);
    return regionName ? `${regionName} - ${localeName}` : localeName;
  });

  watch(locale, () => (isShowMenu.value = false));

  onClickOutside(menuRef, () => (isShowMenu.value = false), {
    ignore: [buttonRef],
  });
</script>
